/* Casanvero — "lab" product sections. Every drawing here is generated from the section's real numbers
   (heights, lengths, sizes, load ratings), so the pictures stay true to the product.
   [data-incline]  ramp height/incline explorer, or jump-vs-steps for stairs
   [data-build]    load rig: counter climbs to the rated load, breed weights light up
   [data-fold]     scroll-scrubbed fold / carry / stairs-to-ramp sequence
   [data-layers]   scroll-scrubbed isometric exploded view of a bed
   [data-sizer]    weight + sleeping style → to-scale footprint and a recommended size */
(() => {
  const doc = document, NS = 'http://www.w3.org/2000/svg';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, k) => a + (b - a) * k;
  const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
  const seg = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
  const f1 = (v) => String(Math.round(v * 10) / 10);
  const P = (q) => q[0].toFixed(1) + ' ' + q[1].toFixed(1);
  const mk = (tag, attrs, parent) => { const e = doc.createElementNS(NS, tag); if (attrs) for (const k in attrs) e.setAttribute(k, attrs[k]); if (parent) parent.appendChild(e); return e; };
  const set = (e, attrs) => { for (const k in attrs) e.setAttribute(k, attrs[k]); };
  const onView = (node, fn, th) => {
    if (reduce || !('IntersectionObserver' in window)) { fn(); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); fn(); } }, { threshold: th || 0.3 });
    io.observe(node);
  };
  const whileVisible = (node, start, stop) => {
    if (!('IntersectionObserver' in window)) { start(); return; }
    new IntersectionObserver((es) => (es[0].isIntersecting ? start() : stop()), { rootMargin: '60px' }).observe(node);
  };
  const animator = () => {
    let id = 0;
    return (from, to, dur, step) => {
      cancelAnimationFrame(id);
      if (reduce) { step(to); return; }
      const t0 = performance.now();
      const f = (now) => { const k = clamp((now - t0) / dur, 0, 1); step(lerp(from, to, 1 - Math.pow(1 - k, 3))); if (k < 1) id = requestAnimationFrame(f); };
      id = requestAnimationFrame(f);
    };
  };

  /* ---------- scroll scrubbing for the tall sticky sections ---------- */
  const scrubs = [];
  const runScrubs = () => {
    const vh = window.innerHeight;
    scrubs.forEach((s) => {
      const r = s.el.getBoundingClientRect();
      if (r.bottom < -80 || r.top > vh + 80) return;
      const p = clamp(-r.top / Math.max(1, r.height - vh), 0, 1);
      if (Math.abs(p - s.p) > 0.0004) { s.p = p; s.fn(p); }
    });
  };
  let ticking = false;
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; runScrubs(); }); } }, { passive: true });
  window.addEventListener('resize', runScrubs);

  /* a glowing "dog" orb with a short comet trail, moved along any path function */
  const orb = (parent, id) => {
    const g = mk('g', { class: 'lab-orb' }, parent);
    const trail = mk('path', { class: 'lab-orb-trail' }, g);
    mk('circle', { r: 16, fill: `url(#lo-${id})`, class: 'lab-orb-halo' }, g);
    const core = mk('circle', { r: 4.5, class: 'lab-orb-core' }, g);
    return { g, trail, core };
  };

  /* ================================================================== incline */
  doc.querySelectorAll('[data-incline]').forEach((sec) => {
    const svg = sec.querySelector('[data-svg]'); if (!svg) return;
    const id = sec.id.replace('lab-', '');
    const out = (k) => sec.querySelector(`[data-out="${k}"]`);
    const setOut = (k, v) => { const e = out(k); if (e) e.textContent = v; };
    const marks = JSON.parse((sec.querySelector('[data-marks]') || {}).textContent || '[]').filter((m) => m.h > 0);
    const H = (sec.dataset.heights || '').split(',').map(parseFloat).filter((v) => v > 0);
    const F = 372, RIGHT = 626;
    const W = mk('g', {}, svg);
    mk('ellipse', { class: 'lb-floorglow', cx: 330, cy: F + 2, rx: 290, ry: 12 }, W);
    mk('path', { class: 'lb-floor', d: `M14 ${F} H${RIGHT + 8}` }, W);

    if (sec.dataset.mode === 'stairs') {
      /* ---- jump vs steps ---- */
      const Ht = H[0] || 15, n = clamp(+sec.dataset.steps || 3, 2, 6), D = parseFloat(sec.dataset.depth) || Ht * 1.4;
      const s = Math.min(236 / Ht, 214 / D), top = F - Ht * s, rise = Ht / n;
      const frac = { 2: 'half', 3: 'third', 4: 'quarter', 5: 'fifth', 6: 'sixth' }[n];
      setOut('rise', f1(rise));
      mk('path', { class: 'lb-divider', d: `M318 40 V${F - 6}` }, W);
      const panel = (x0, x1, label) => { const g = mk('g', { class: 'lb-panel' }, W); const t = mk('text', { class: 'lb-panel-lbl', x: (x0 + x1) / 2, y: 44, 'text-anchor': 'middle' }, g); t.textContent = label; return g; };
      const sofa = (g, x0, x1) => {
        mk('path', { class: 'lb-sofa', d: `M${x0} ${F} V${top + 12} Q${x0} ${top} ${x0 + 12} ${top} H${x1} V${F} Z` }, g);
        mk('path', { class: 'lb-sofa-cush', d: `M${x0 + 6} ${top + 16} H${x1}` }, g);
        const l = mk('text', { class: 'lb-plat-lbl', x: (x0 + x1) / 2 + 4, y: top - 12, 'text-anchor': 'middle' }, g); l.textContent = sec.dataset.platform || 'Sofa';
      };
      // left: the jump
      const gJ = panel(20, 300, 'One jump'); sofa(gJ, 196, 304);
      const jA = [196, top], jB = [70, F];
      const jump = (k) => { const x = lerp(jA[0], jB[0], k); const y = lerp(jA[1], jB[1], k * k) - Math.sin(k * Math.PI) * 26; return [x, y]; };
      let d = ''; for (let i = 0; i <= 24; i++) { const q = jump(i / 24); d += (i ? ' L' : 'M') + P(q); }
      mk('path', { class: 'lb-path', d }, gJ);
      mk('path', { class: 'dim-l', d: `M150 ${top} V${F} M144 ${top} H156 M144 ${F} H156` }, gJ);
      const jt = mk('text', { class: 'lb-dim-t', x: 142, y: (top + F) / 2 + 4, 'text-anchor': 'end' }, gJ); jt.textContent = f1(Ht) + ' in';
      mk('circle', { class: 'lb-impact', cx: jB[0], cy: F, r: 10 }, gJ);
      // right: the stairs
      const gS = panel(338, 626, `${n} steps`);
      const sx1 = 560, sx0 = sx1 - D * s, tread = (D * s) / n, rpx = (Ht * s) / n;
      sofa(gS, sx1, 630);
      let prof = `M${sx0} ${F}`;
      for (let i = 0; i < n; i++) { const y = F - (i + 1) * rpx; prof += ` V${y + 6} Q${sx0 + i * tread} ${y} ${sx0 + i * tread + 6} ${y} H${sx0 + (i + 1) * tread}`; }
      prof += ` V${F} Z`;
      const wood = sec.dataset.look === 'wood';
      mk('path', wood ? { class: 'lb-woodstair', d: prof, fill: `url(#lw-${id})` } : { class: 'lb-foam', d: prof }, gS);
      for (let i = 0; i < n; i++) {
        const y = F - (i + 1) * rpx;
        mk('path', wood ? { class: 'lb-tread', d: `M${sx0 + i * tread + 4} ${y + 1} H${sx0 + (i + 1) * tread - 1}` } : { class: 'lb-foam-hi', d: `M${sx0 + i * tread + 8} ${y + 1.5} H${sx0 + (i + 1) * tread - 4}` }, gS);
      }
      const rx = sx0 - 16; mk('path', { class: 'dim-l', d: `M${rx} ${F - rpx} V${F} M${rx - 6} ${F - rpx} H${rx + 6} M${rx - 6} ${F} H${rx + 6}` }, gS);
      const st = mk('text', { class: 'lb-dim-t', x: rx - 8, y: F - rpx / 2 + 4, 'text-anchor': 'end' }, gS); st.textContent = f1(rise) + ' in';
      // the path down the stairs: hop from each tread to the next
      const stepPts = [[sx1 + 6, top]];
      for (let i = n - 1; i >= 0; i--) stepPts.push([sx0 + i * tread + tread * 0.5, F - (i + 1) * rpx]);
      stepPts.push([sx0 - 34, F]);
      const stepAt = (k) => { const m = stepPts.length - 1, t = clamp(k, 0, 0.9999) * m, i = Math.floor(t), u = t - i; const a = stepPts[i], b = stepPts[i + 1]; return [lerp(a[0], b[0], u), lerp(a[1], b[1], u * u) - Math.sin(u * Math.PI) * 10]; };
      d = ''; for (let i = 0; i <= 60; i++) d += (i ? ' L' : 'M') + P(stepAt(i / 60));
      mk('path', { class: 'lb-path', d }, gS);
      const o = orb(W, id);
      let way = 'steps', t0 = 0, raf = 0, auto = true, autoT = 0;
      const btns = [...sec.querySelectorAll('[data-way]')];
      const choose = (w) => {
        way = w; t0 = performance.now();
        btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.way === w)));
        gJ.classList.toggle('is-dim', w !== 'jump'); gS.classList.toggle('is-dim', w !== 'steps');
        setOut('f', w === 'jump' ? `One ${f1(Ht)} in drop.` : `A ${frac} of the drop per move.`);
        setOut('n', w === 'jump' ? 'Every landing takes the full height, on the front legs first.' : `Each move down is ${f1(rise)} in, not ${f1(Ht)} in.`);
      };
      btns.forEach((b) => b.addEventListener('click', () => { auto = false; choose(b.dataset.way); }));
      choose('steps');
      const frame = (now) => {
        const dur = way === 'jump' ? 1500 : 2600, k = ((now - t0) % (dur + 700)) / dur;
        const q = way === 'jump' ? jump(clamp(k, 0, 1)) : stepAt(clamp(k, 0, 1));
        const q2 = way === 'jump' ? jump(clamp(k - 0.12, 0, 1)) : stepAt(clamp(k - 0.08, 0, 1));
        set(o.g, { transform: `translate(${q[0].toFixed(1)} ${(q[1] - 6).toFixed(1)})`, opacity: k > 1 ? 0 : 1 });
        set(o.trail, { d: `M${(q2[0] - q[0]).toFixed(1)} ${(q2[1] - q[1]).toFixed(1)} L0 0` });
        if (auto && now - autoT > 4200) { autoT = now; choose(way === 'steps' ? 'jump' : 'steps'); }
        raf = requestAnimationFrame(frame);
      };
      whileVisible(sec, () => { if (!raf && !reduce) { autoT = performance.now(); raf = requestAnimationFrame(frame); } }, () => { cancelAnimationFrame(raf); raf = 0; });
      if (reduce) set(o.g, { opacity: 0 });
      return;
    }

    /* ---- ramp height settings ---- */
    const L = parseFloat(sec.dataset.len) || 36, X0 = 74;
    const hmax = Math.max(...H, ...marks.map((m) => m.h));
    const s = Math.min(286 / hmax, 440 / L), Lpx = L * s, T = 10, C = 5;
    const markEls = marks.map((m) => {
      const y = F - m.h * s, g = mk('g', { class: 'lb-mark' }, W);
      mk('path', { d: `M16 ${y} H${RIGHT}` }, g);
      const t = mk('text', { x: 18, y: y - 7 }, g); t.textContent = `${m.l} · ${f1(m.h)} in`;
      return { g, h: m.h };
    });
    const plat = mk('g', { class: 'lb-plat' }, W);
    const platBody = mk('path', { fill: `url(#lp-${id})` }, plat);
    const platBand = mk('rect', { class: 'lb-plat-band', rx: 7, height: 14 }, plat);
    const platLbl = mk('text', { class: 'lb-plat-lbl', 'text-anchor': 'middle' }, plat); platLbl.textContent = sec.dataset.platform || 'Bed or sofa';
    const hDim = mk('path', { class: 'dim-l' }, W);
    const hTxt = mk('text', { class: 'lb-dim-t lb-dim-big', 'text-anchor': 'end' }, W);
    const leg = mk('path', { class: 'lb-leg' }, W);
    const board = mk('path', { fill: `url(#lw-${id})`, class: 'lb-board' }, W);
    const carpet = mk('path', { class: 'lb-carpet' }, W);
    const ribs = mk('path', { class: 'lb-ribs' }, W);
    const arc = mk('path', { class: 'lb-arc' }, W);
    const arcFill = mk('path', { class: 'lb-arc-fill' }, W);
    const aTxt = mk('text', { class: 'lb-angle' }, W);
    const run = mk('path', { class: 'dim-l' }, W);
    const runTxt = mk('text', { class: 'lb-dim-t', 'text-anchor': 'middle' }, W);
    const o = orb(W, id);
    let geo = null;
    const draw = (h) => {
      const th = Math.asin(clamp(h / L, 0.02, 0.98)), c = Math.cos(th), sn = Math.sin(th);
      const u = [c, -sn], nn = [-sn, -c];
      const A = [X0, F], at = (d, off) => [A[0] + u[0] * d + nn[0] * off, A[1] + u[1] * d + nn[1] * off];
      const B = at(Lpx, 0), Ty = B[1], Tx = B[0];
      geo = { at, th };
      set(platBody, { d: `M${Tx - 2} ${F} V${Ty + 10} Q${Tx - 2} ${Ty} ${Tx + 8} ${Ty} H${RIGHT + 8} V${F} Z` });
      set(platBand, { x: Tx + 4, y: Ty - 2, width: Math.max(0, RIGHT - Tx) });
      set(platLbl, { x: (Tx + RIGHT) / 2 + 4, y: Ty - 14 });
      set(hDim, { d: `M${RIGHT - 14} ${Ty} V${F} M${RIGHT - 20} ${Ty} H${RIGHT - 8} M${RIGHT - 20} ${F} H${RIGHT - 8}` });
      set(hTxt, { x: RIGHT - 24, y: (Ty + F) / 2 + 8 }); hTxt.textContent = f1(h) + '″';
      const Pk = at(Lpx * 0.8, 0), Q = at(Lpx * 0.5, 0);
      set(leg, { d: `M${P(Pk)} L${Pk[0].toFixed(1)} ${F - 3} L${P(Q)} M${Pk[0] - 9} ${F - 2} H${Pk[0] + 9}` });
      set(board, { d: `M${P(A)} L${P(B)} L${P(at(Lpx, T))} L${P(at(0, T))} Z` });
      set(carpet, { d: `M${P(at(3, T))} L${P(at(Lpx - 3, T))} L${P(at(Lpx - 3, T + C))} L${P(at(3, T + C))} Z` });
      let rd = ''; for (let dd = 16; dd < Lpx - 6; dd += 17) rd += `M${P(at(dd, T))} L${P(at(dd, T + C + 1.5))}`;
      set(ribs, { d: rd });
      const r = 66, ex = X0 + r * c, ey = F - r * sn;
      set(arc, { d: `M${X0 + r} ${F} A${r} ${r} 0 0 0 ${ex.toFixed(1)} ${ey.toFixed(1)}` });
      set(arcFill, { d: `M${X0} ${F} L${X0 + r} ${F} A${r} ${r} 0 0 0 ${ex.toFixed(1)} ${ey.toFixed(1)} Z` });
      const deg = (th * 180) / Math.PI;
      set(aTxt, { x: X0 + (r + 16) * Math.cos(th / 2) + 2, y: F - (r + 16) * Math.sin(th / 2) + 5 }); aTxt.textContent = Math.round(deg) + '°';
      set(run, { d: `M${X0} ${F + 24} H${Tx} M${X0} ${F + 18} V${F + 30} M${Tx} ${F + 18} V${F + 30}` });
      set(runTxt, { x: (X0 + Tx) / 2, y: F + 46 }); runTxt.textContent = `Floor run ${f1(L * c)} in`;
      markEls.forEach((m) => m.g.classList.toggle('is-near', Math.abs(m.h - h) < 1.6));
      setOut('h', f1(h)); setOut('a', Math.round(deg)); setOut('r', f1(L * c));
      const feel = deg <= 22 ? ['Gentle.', 'Easy going for seniors, short legs and dogs coming back from an injury.', 'is-good'] : deg <= 30 ? ['Moderate.', 'Comfortable for most adult dogs. Older dogs may prefer a setting lower.', 'is-mid'] : ['Steep.', 'Best for confident, agile dogs. Use a lower setting where you can.', 'is-steep'];
      setOut('f', feel[0]); setOut('n', feel[1]);
      const dot = out('dot'); if (dot) dot.className = 'lab-feel-dot ' + feel[2];
      sec.style.setProperty('--deg', deg.toFixed(1));
    };
    const btns = [...sec.querySelectorAll('[data-h]')];
    let cur = H[clamp(+sec.dataset.start || 0, 0, H.length - 1)] || H[0], idx = H.indexOf(cur), auto = true;
    const a = animator();
    const go = (i, dur) => {
      idx = i; btns.forEach((b, k) => b.setAttribute('aria-pressed', String(k === i)));
      const from = cur; cur = H[i]; a(from, cur, dur == null ? 900 : dur, draw);
    };
    btns.forEach((b, k) => b.addEventListener('click', () => { auto = false; go(k); }));
    draw(H[0]); btns.forEach((b, k) => b.setAttribute('aria-pressed', String(k === idx)));
    onView(sec, () => {
      // a quick run through every setting, then settle on the default
      if (reduce) { go(idx, 0); return; }
      const target = idx; let i = 0; cur = H[0];
      const next = () => { if (!auto) return; if (i < H.length) { go(i, 620); i++; setTimeout(next, 700); } else go(target, 1100); };
      next();
    }, 0.35);
    // the orb walks up the ramp while the section is on screen
    let raf = 0; const t0 = performance.now();
    const frame = (now) => {
      if (geo) {
        const k = ((now - t0) % 3400) / 2600, kk = clamp(k, 0, 1), e = kk * kk * (3 - 2 * kk);
        const q = geo.at(lerp(-40, Lpx + 24, e), T + C + 7), q2 = geo.at(lerp(-40, Lpx + 24, Math.max(0, e - 0.09)), T + C + 7);
        const vis = k > 1 ? 0 : Math.min(1, e * 6, (1 - e) * 8);
        set(o.g, { transform: `translate(${q[0].toFixed(1)} ${q[1].toFixed(1)})`, opacity: vis.toFixed(2) });
        set(o.trail, { d: `M${(q2[0] - q[0]).toFixed(1)} ${(q2[1] - q[1]).toFixed(1)} L0 0` });
      }
      raf = requestAnimationFrame(frame);
    };
    whileVisible(sec, () => { if (!raf && !reduce) raf = requestAnimationFrame(frame); }, () => { cancelAnimationFrame(raf); raf = 0; });
    if (reduce) set(o.g, { opacity: 0 });
  });

  /* ================================================================== load rig */
  doc.querySelectorAll('[data-build]').forEach((sec) => {
    const R = +sec.dataset.rating || 100, rig = sec.querySelector('.lab-rig'); if (!rig) return;
    const num = sec.querySelector('[data-count]'), fill = sec.querySelector('[data-fill]'), dots = [...sec.querySelectorAll('[data-w]')];
    const a = animator();
    const paint = (v) => {
      if (num) num.textContent = Math.round(v);
      if (fill) fill.style.transform = `scaleX(${(v / R).toFixed(4)})`;
      dots.forEach((d) => d.classList.toggle('is-on', v >= +d.dataset.w));
      rig.classList.toggle('is-full', v >= R - 0.5);
    };
    paint(0);
    onView(rig, () => { rig.classList.add('is-run'); setTimeout(() => a(0, R, 2600, paint), reduce ? 0 : 900); }, 0.35);
  });

  /* ================================================================== fold sequence */
  doc.querySelectorAll('[data-fold]').forEach((sec) => {
    const svg = sec.querySelector('[data-svg]'); if (!svg) return;
    const id = sec.id.replace('lab-', ''), mode = sec.dataset.mode || 'fold';
    const caps = [...sec.querySelectorAll('[data-cap]')], prog = sec.querySelector('.lab-prog i');
    const dOpen = sec.querySelector('.lab-dim--open'), dFold = sec.querySelector('.lab-dim--fold');
    const F = 362, G = mk('g', {}, svg);
    mk('ellipse', { class: 'lb-floorglow', cx: 320, cy: F + 2, rx: 280, ry: 12 }, G);
    mk('path', { class: 'lb-floor', d: `M16 ${F} H624` }, G);
    // a bed to slide under (fold mode)
    const bed = mk('g', { class: 'fd-bed' }, G);
    mk('path', { class: 'fd-bed-frame', d: `M392 ${F} V${F - 44} M616 ${F} V${F - 44} M384 ${F - 44} H640` }, bed);
    mk('rect', { class: 'fd-bed-base', x: 384, y: F - 92, width: 262, height: 48, rx: 6 }, bed);
    mk('rect', { class: 'fd-bed-mat', x: 380, y: F - 136, width: 266, height: 46, rx: 18 }, bed);
    const shape = mk('g', { class: 'fd-shape' }, G);
    const leg = mk('path', { class: 'lb-leg' }, shape);
    const body = mk('path', { fill: `url(#fw-${id})`, class: 'lb-board' }, shape);
    const carpet = mk('path', { class: 'lb-carpet' }, shape);
    const ribs = mk('path', { class: 'lb-ribs' }, shape);
    const treads = mk('path', { class: 'fd-treads' }, shape);
    const handle = mk('path', { class: 'fd-handle' }, shape);
    const dimL = mk('path', { class: 'dim-l fd-dim' }, G);
    const dimT = mk('path', { class: 'dim-l fd-dim' }, G);
    const setCaps = (p) => {
      const n = caps.length; if (!n) return;
      const i = Math.min(n - 1, Math.floor(seg(p, 0.02, 0.96) * n));
      caps.forEach((c, k) => { c.classList.toggle('is-now', k === i); c.classList.toggle('is-done', k < i); });
      if (prog) prog.style.transform = `scaleX(${p.toFixed(4)})`;
      if (dOpen) dOpen.classList.toggle('is-on', p < 0.16);
      if (dFold) dFold.classList.toggle('is-on', p > 0.8);
    };

    let render;
    if (mode === 'convert') {
      const n = 3, rp = 64, td = 76, X = 176, topX = X + n * td + 26, Ftop = F - n * rp;
      const stairsTop = [[X, F]];
      for (let i = 0; i < n; i++) { stairsTop.push([X + i * td, F - (i + 1) * rp]); stairsTop.push([X + (i + 1) * td, F - (i + 1) * rp]); }
      stairsTop[stairsTop.length - 1] = [topX, Ftop];
      const rA = [X - 70, F], rB = [topX, Ftop];
      const rampTop = stairsTop.map((q, j) => (j === 0 ? rA : [lerp(rA[0], rB[0], j / (stairsTop.length - 1)), lerp(rA[1], rB[1], j / (stairsTop.length - 1))]));
      render = (p) => {
        const k1 = ease(seg(p, 0.14, 0.5)), k2 = ease(seg(p, 0.58, 0.86));
        const flat = (y) => F - (F - y) * lerp(1, 0.1, k2);
        const top = stairsTop.map((q, j) => [lerp(q[0], rampTop[j][0], k1), flat(lerp(q[1], rampTop[j][1], k1))]);
        let d = `M${P(top[0])}`; for (let j = 1; j < top.length; j++) d += ` L${P(top[j])}`;
        d += ` L${topX} ${F} Z`;
        set(body, { d });
        let td2 = ''; for (let i = 0; i < n; i++) { const a = top[2 * i + 1], b = top[2 * i + 2]; td2 += `M${P(a)} L${P(b)}`; }
        set(treads, { d: td2 });
        set(leg, { d: '' }); set(carpet, { d: '' }); set(ribs, { d: '' }); set(handle, { d: '' });
        const k3 = seg(p, 0.84, 0.96), yT = flat(Ftop);
        set(dimL, { d: `M${X - 70} ${F + 26} H${topX} M${X - 70} ${F + 20} V${F + 32} M${topX} ${F + 20} V${F + 32}`, opacity: k3 });
        set(dimT, { d: `M${topX + 18} ${yT} V${F} M${topX + 12} ${yT} H${topX + 24} M${topX + 12} ${F} H${topX + 24}`, opacity: k3 });
        bed.style.opacity = 0;
      };
    } else {
      const Lp = 380, T = 12, C = 5, th0 = (27 * Math.PI) / 180, hd = Lp * 0.8, legL = hd * Math.sin(th0);
      const rot = (q, pv, a) => { const c = Math.cos(a), s = Math.sin(a), x = q[0] - pv[0], y = q[1] - pv[1]; return [pv[0] + x * c - y * s, pv[1] + x * s + y * c]; };
      render = (p) => {
        const isCase = mode === 'case';
        // the support tucks up under the board first, then the board comes down with it
        const kA = ease(seg(p, 0.1, 0.28)), k1 = ease(seg(p, 0.3, isCase ? 0.42 : 0.52));
        const kh = isCase ? ease(seg(p, 0.45, 0.64)) : 0; // fold in half
        const kl = isCase ? ease(seg(p, 0.66, 0.8)) : 0; // lift by the handle
        const k2 = mode === 'fold' ? ease(seg(p, 0.56, 0.8)) : 0; // slide under the bed
        const k3 = seg(p, 0.82, 0.96);
        const th = lerp(th0, 0, k1), c = Math.cos(th), sn = Math.sin(th);
        const u = [c, -sn], nn = [-sn, -c];
        const lift = 8 * k1;
        const ox = mode === 'fold' ? lerp(120, 300, k2) : isCase ? lerp(120, 210, kh) : 120, oy = -lerp(0, 92, kl);
        const A = [ox, F - lift + oy], raw = (d, off) => [A[0] + u[0] * d + nn[0] * off, A[1] + u[1] * d + nn[1] * off];
        // the far half swings up and over on the hinge at the middle of the walking surface
        const half = Lp / 2, pv = raw(half, T + C), phi = -Math.PI * kh;
        const at = (d, off) => (d > half + 0.01 && kh > 0 ? rot(raw(d, off), pv, phi) : raw(d, off));
        const H0 = at(hd, 0), psi = lerp(Math.PI / 2, Math.PI - th0, kA) + (th0 - th) + (hd > half ? phi : 0);
        const Lend = [H0[0] + Math.cos(psi) * legL, H0[1] + Math.sin(psi) * legL];
        set(leg, { d: `M${P(H0)} L${P(Lend)}` });
        const quad = (d0, d1, o0, o1) => `M${P(at(d0, o0))} L${P(at(d1, o0))} L${P(at(d1, o1))} L${P(at(d0, o1))} Z`;
        set(body, { d: isCase ? quad(0, half, 0, T) + quad(half + 0.02, Lp, 0, T) : quad(0, Lp, 0, T) });
        set(carpet, { d: isCase ? quad(3, half - 1, T, T + C) + quad(half + 1, Lp - 3, T, T + C) : quad(3, Lp - 3, T, T + C) });
        let rd = ''; for (let dd = 16; dd < Lp - 6; dd += 17) rd += `M${P(at(dd, T))} L${P(at(dd, T + C + 1.5))}`;
        set(ribs, { d: rd });
        set(treads, { d: '' });
        const stackTop = A[1] - (T + C) * (1 + kh) - 2 - (T - 2) * kh, hx = A[0] + half / 2;
        set(handle, { d: isCase && kl > 0.02 ? `M${(hx - 26).toFixed(1)} ${stackTop.toFixed(1)} v-${(8 + 10 * kl).toFixed(1)} a10 10 0 0 1 10 -10 h32 a10 10 0 0 1 10 10 v${(8 + 10 * kl).toFixed(1)}` : '', opacity: kl });
        bed.style.opacity = mode === 'fold' ? seg(p, 0.46, 0.58) : 0;
        const len = isCase ? half : Lp, yb = A[1];
        set(dimL, { d: `M${ox} ${yb + 26} H${ox + len} M${ox} ${yb + 20} V${yb + 32} M${ox + len} ${yb + 20} V${yb + 32}`, opacity: mode === 'fold' ? 0 : k3 });
        set(dimT, { d: `M${ox - 18} ${stackTop} V${yb} M${ox - 24} ${stackTop} H${ox - 12} M${ox - 24} ${yb} H${ox - 12}`, opacity: isCase ? k3 : 0 });
      };
    }
    scrubs.push({ el: sec, p: -1, fn: (p) => { render(p); setCaps(p); } });
    render(0); setCaps(0);
  });

  /* ================================================================== exploded layers */
  const hexRgb = (h) => { const m = /^#?([0-9a-f]{6})$/i.exec(h || ''); const n = m ? parseInt(m[1], 16) : 0x8a8580; return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const rgb = (c, k) => `rgb(${c.map((v) => Math.round(clamp(v * k, 0, 255))).join(',')})`;
  doc.querySelectorAll('[data-layers]').forEach((sec) => {
    const svg = sec.querySelector('[data-svg]'); if (!svg) return;
    const id = sec.id.replace('lab-', '');
    const data = JSON.parse((sec.querySelector('[data-layer-data]') || {}).textContent || '[]');
    const items = [...sec.querySelectorAll('[data-layer-item]')];
    const oval = sec.dataset.shape === 'oval', ratio = (+sec.dataset.ratio || 13) / 10;
    const N = 96, ex = oval ? 2 : 7, A0 = 206, B0 = A0 / ratio, K = 0.84, cx = 320, baseY = 398, c30 = Math.cos(Math.PI / 6);
    const proj = (x, y, z) => [cx + (x - y) * c30 * K, baseY + (x + y) * 0.5 * K - z];
    const ring = (a, b) => { const out = []; for (let i = 0; i < N; i++) { const t = (i / N) * Math.PI * 2, c = Math.cos(t), s = Math.sin(t); out.push([a * Math.sign(c) * Math.pow(Math.abs(c), 2 / ex), b * Math.sign(s) * Math.pow(Math.abs(s), 2 / ex)]); } return out; };
    const poly = (arr, z) => arr.map((q) => P(proj(q[0], q[1], z))).join(' L');
    const total = data.reduce((s, l) => s + (+l.t || 10), 0);
    const gap = clamp((baseY - 120 - total - 24) / Math.max(1, data.length - 1), 26, 64);
    const light = [0.28, 1]; const ll = Math.hypot(light[0], light[1]);
    const groups = [];
    let z = 0;
    for (let bi = data.length - 1; bi >= 0; bi--) {
      const Ld = data[bi], t = +Ld.t || 10, sc = (+Ld.in || 100) / 100, col = hexRgb(Ld.c);
      const g = mk('g', { class: 'ly' }, svg);
      const outer = ring(A0 * sc, B0 * sc), z0 = z, z1 = z + t;
      mk('path', { d: 'M' + poly(outer, z0) + ' Z', fill: rgb(col, 0.32), class: 'ly-shadow' }, g);
      for (let i = 0; i < N; i++) {
        const a = outer[i], b = outer[(i + 1) % N];
        let nx = b[1] - a[1], ny = -(b[0] - a[0]); const nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl;
        if ((a[0] + b[0]) * nx + (a[1] + b[1]) * ny < 0) { nx = -nx; ny = -ny; }
        if (nx + ny <= 0) continue;
        const sh = 0.5 + 0.42 * Math.max(0, (nx * light[0] + ny * light[1]) / ll);
        const pa = proj(a[0], a[1], z1), pb = proj(b[0], b[1], z1), pc = proj(b[0], b[1], z0), pd = proj(a[0], a[1], z0);
        mk('path', { d: `M${P(pa)} L${P(pb)} L${P(pc)} L${P(pd)} Z`, fill: rgb(col, sh * 0.82), stroke: rgb(col, sh * 0.82), 'stroke-width': 0.6 }, g);
      }
      let topD = 'M' + poly(outer, z1) + ' Z';
      if (Ld.ring) {
        const inner = ring(A0 * sc * 0.7, B0 * sc * 0.62);
        // the far inside wall shows through the opening
        for (let i = 0; i < N; i++) {
          const a = inner[i], b = inner[(i + 1) % N];
          let nx = b[1] - a[1], ny = -(b[0] - a[0]); const nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl;
          if ((a[0] + b[0]) * nx + (a[1] + b[1]) * ny < 0) { nx = -nx; ny = -ny; }
          if (nx + ny >= 0) continue;
          const zb = z1 - t * 0.85;
          mk('path', { d: `M${P(proj(a[0], a[1], z1))} L${P(proj(b[0], b[1], z1))} L${P(proj(b[0], b[1], zb))} L${P(proj(a[0], a[1], zb))} Z`, fill: rgb(col, 0.55), stroke: rgb(col, 0.55), 'stroke-width': 0.6 }, g);
        }
        topD += ' M' + poly(inner.slice().reverse(), z1) + ' Z';
      }
      mk('path', { d: topD, fill: rgb(col, 1.08), 'fill-rule': 'evenodd', class: 'ly-top' }, g);
      if (Ld.x && Ld.x !== 'none') mk('path', { d: topD, fill: `url(#tx-${Ld.x}-${id})`, 'fill-rule': 'evenodd', class: 'ly-tex' }, g);
      mk('path', { d: topD, fill: 'none', 'fill-rule': 'evenodd', class: 'ly-edge' }, g);
      groups[bi] = { g, b: data.length - 1 - bi };
      z = z1;
    }
    const render = (p) => {
      const e = ease(seg(p, 0.06, 0.62)), n = data.length;
      groups.forEach((G) => { G.g.setAttribute('transform', `translate(0 ${(-G.b * gap * e).toFixed(1)})`); });
      const i = Math.min(n - 1, Math.floor(seg(p, 0.12, 0.97) * n));
      groups.forEach((G, k) => G.g.classList.toggle('is-now', k === i && p > 0.1));
      items.forEach((it, k) => { it.classList.toggle('is-now', k === i && p > 0.1); it.classList.toggle('is-done', k < i); });
    };
    scrubs.push({ el: sec, p: -1, fn: render });
    render(0);
  });

  /* ================================================================== size finder */
  const BREEDS = [[8, 'e.g. Chihuahua, Yorkie'], [15, 'e.g. Shih Tzu, Maltese'], [24, 'e.g. Pug, French Bulldog'], [32, 'e.g. Beagle, Corgi'], [50, 'e.g. Border Collie, Spaniel'], [75, 'e.g. Labrador, Golden Retriever'], [95, 'e.g. German Shepherd'], [999, 'Large and giant breeds']];
  doc.querySelectorAll('[data-sizer]').forEach((sec) => {
    const svg = sec.querySelector('[data-svg]'); if (!svg) return;
    const id = sec.id.replace('lab-', '');
    const sizes = JSON.parse((sec.querySelector('[data-sizes]') || {}).textContent || '[]').filter((z) => z.l > 0 && z.w > 0).sort((a, b) => a.l * a.w - b.l * b.w);
    if (!sizes.length) return;
    const oval = sec.dataset.shape === 'oval', inner = (+sec.dataset.inner || 96) / 100;
    const range = sec.querySelector('[data-weight]'), styleBtns = [...sec.querySelectorAll('[data-style]')], choose = sec.querySelector('[data-choose]');
    const out = (k, v) => { const e = sec.querySelector(`[data-out="${k}"]`); if (e) e.textContent = v; };
    const maxL = Math.max(...sizes.map((z) => z.l)), maxW = Math.max(...sizes.map((z) => z.w));
    const sc = Math.min(520 / maxL, 380 / maxW), cx = 300, cy = 250;
    const beds = sizes.slice().reverse().map((z) => {
      const g = mk('g', { class: 'sz-bed' }, svg), w = z.l * sc, h = z.w * sc;
      if (oval) { mk('ellipse', { class: 'sz-out', cx, cy, rx: w / 2, ry: h / 2 }, g); mk('ellipse', { class: 'sz-in', cx, cy, rx: (w / 2) * inner, ry: (h / 2) * inner }, g); }
      else { mk('rect', { class: 'sz-out', x: cx - w / 2, y: cy - h / 2, width: w, height: h, rx: Math.min(26, h / 6) }, g); }
      return { z, g, h };
    });
    const dog = mk('g', { class: 'sz-dog' }, svg);
    const dogE = mk('ellipse', { cx, cy, fill: `url(#szd-${id})`, class: 'sz-dog-e' }, dog);
    const dogR = mk('ellipse', { cx, cy, class: 'sz-dog-ring' }, dog);
    const lblG = mk('g', {}, svg);
    beds.forEach((b) => { const t = mk('text', { class: 'sz-lbl', x: cx, y: cy - b.h / 2 - 9, 'text-anchor': 'middle' }, lblG); t.textContent = b.z.n; b.t = t; });
    const dimW = mk('path', { class: 'sz-dim' }, svg), dimH = mk('path', { class: 'sz-dim' }, svg);
    const dimWT = mk('text', { class: 'sz-dim-t', 'text-anchor': 'middle' }, svg), dimHT = mk('text', { class: 'sz-dim-t', 'text-anchor': 'start' }, svg);
    let style = 'curl', pick = null, er = [0, 0];
    const a = animator();
    const update = () => {
      const lb = +range.value, kg = lb / 2.2046, body = 29 * Math.cbrt(kg);
      const fp = style === 'curl' ? [body * 0.62, body * 0.62] : [body * 1.1, body * 0.52];
      const fits = (z) => fp[0] <= z.l * inner && fp[1] <= z.w * inner;
      const okW = (z) => !z.max || lb <= z.max;
      pick = sizes.find((z) => fits(z) && okW(z)) || null;
      let verdict;
      if (pick) {
        const spare = Math.min(pick.l * inner - fp[0], pick.w * inner - fp[1]);
        verdict = style === 'curl' ? (spare > 14 ? 'Room to spare when they curl up.' : 'A snug, nest-like fit when they curl up.') : (spare > 14 ? 'Room to stretch out, legs and all.' : 'Just enough length to stretch out fully.');
        if (pick.max) verdict += ` Rated up to ${pick.max} lb.`;
      } else {
        const curlFits = sizes.find((z) => okW(z) && body * 0.62 <= z.l * inner && body * 0.62 <= z.w * inner);
        pick = curlFits || sizes[sizes.length - 1];
        verdict = style === 'stretch' && curlFits ? (oval ? `This bed is made for curling up. At ${lb} lb there isn't room to lie fully stretched out, so a flat mattress suits a sprawler better.` : `At ${lb} lb they'll fit curled up, but not fully stretched out.`) : `At ${lb} lb your dog may be bigger than our largest size. Measure them nose to tail and compare with the ${pick.n}.`;
      }
      const inch = (cm) => f1(cm / 2.54);
      out('w', lb); out('kg', `${f1(kg)} kg`);
      out('breed', (BREEDS.find((b) => lb <= b[0]) || BREEDS[BREEDS.length - 1])[1]);
      out('size', pick.n); out('dims', `${pick.l} × ${pick.w}${pick.h ? ' × ' + pick.h : ''} cm · ${inch(pick.l)} × ${inch(pick.w)} in`);
      out('verdict', verdict);
      beds.forEach((b) => { b.g.classList.toggle('is-pick', b.z === pick); b.t.classList.toggle('is-pick', b.z === pick); });
      const w = pick.l * sc, h = pick.w * sc;
      set(dimW, { d: `M${cx - w / 2} ${cy + h / 2 + 22} H${cx + w / 2} M${cx - w / 2} ${cy + h / 2 + 16} V${cy + h / 2 + 28} M${cx + w / 2} ${cy + h / 2 + 16} V${cy + h / 2 + 28}` });
      set(dimWT, { x: cx, y: cy + h / 2 + 44 }); dimWT.textContent = `${pick.l} cm`;
      set(dimH, { d: `M${cx + w / 2 + 20} ${cy - h / 2} V${cy + h / 2} M${cx + w / 2 + 14} ${cy - h / 2} H${cx + w / 2 + 26} M${cx + w / 2 + 14} ${cy + h / 2} H${cx + w / 2 + 26}` });
      set(dimHT, { x: cx + w / 2 + 30, y: cy + 4 }); dimHT.textContent = `${pick.w} cm`;
      const to = [(fp[0] / 2) * sc, (fp[1] / 2) * sc], from = er.slice();
      a(0, 1, 650, (k) => { er = [lerp(from[0], to[0], k), lerp(from[1], to[1], k)]; set(dogE, { rx: er[0].toFixed(1), ry: er[1].toFixed(1) }); set(dogR, { rx: er[0].toFixed(1), ry: er[1].toFixed(1) }); });
      range.style.setProperty('--fill', ((lb - range.min) / (range.max - range.min) * 100).toFixed(1) + '%');
      if (choose) { choose.classList.remove('is-done'); choose.firstChild.textContent = `Choose ${pick.n} `; }
    };
    range.addEventListener('input', update);
    styleBtns.forEach((b) => b.addEventListener('click', () => { style = b.dataset.style; styleBtns.forEach((x) => x.setAttribute('aria-pressed', String(x === b))); update(); }));
    if (choose) choose.addEventListener('click', () => {
      if (!pick || !pick.v) return;
      const v = pick.v.trim().toLowerCase();
      const chip = [...doc.querySelectorAll('.option-chip[data-option-value]')].find((c) => c.dataset.optionValue.trim().toLowerCase() === v);
      if (chip) { chip.click(); (chip.closest('.option-switchers') || chip).scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); }
      else {
        const sel = doc.querySelector('[data-variant-select]');
        const opt = sel && [...sel.options].find((o) => (o.dataset.options || '').toLowerCase().split('|||').includes(v));
        if (opt) { sel.value = opt.value; sel.dispatchEvent(new Event('change', { bubbles: true })); sel.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); }
      }
      choose.classList.add('is-done'); choose.firstChild.textContent = `${pick.n} selected `;
    });
    update();
  });

  /* ================================================================== foam press test */
  doc.querySelectorAll('[data-foam]').forEach((sec) => {
    const svg = sec.querySelector('[data-svg]'); if (!svg) return;
    const id = sec.id.replace('lab-', '');
    const T0 = 246, B = 318, R = 40, HALF = 132;
    const G = mk('g', {}, svg);
    mk('path', { class: 'lb-floor', d: `M24 ${B + 4} H616` }, G);
    const station = (cx, label, fill, tex, depth, profile, rebound) => {
      const g = mk('g', {}, G);
      const t = mk('text', { class: 'fm-lbl', x: cx, y: 70, 'text-anchor': 'middle' }, g); t.textContent = label;
      const press = mk('ellipse', { cx, cy: B + 16, fill: `url(#fp-${id})`, class: 'fm-press' }, g);
      const slab = mk('path', { fill: `url(#${fill}-${id})`, class: 'fm-slab' }, g);
      const texture = mk('path', { fill: `url(#${tex}-${id})` }, g);
      const shadow = mk('ellipse', { cx, class: 'fm-shadow' }, g);
      const orb = mk('g', {}, g);
      mk('circle', { r: R, fill: `url(#fo-${id})` }, orb);
      mk('ellipse', { cx: -13, cy: -15, rx: 12, ry: 7, fill: 'rgba(255,255,255,0.16)' }, orb);
      const ring = mk('circle', { cx, cy: B - 2, r: 6, class: 'fm-ring' }, g);
      return { cx, depth, profile, rebound, press, slab, texture, shadow, orb, ring, d: 0 };
    };
    // fibre: a narrow, deep dip; foam: a wide cradle that follows the weight's curve
    const S = [
      station(170, 'Fibre fill', 'fw', 'ff', 56, (dx) => Math.exp(-(dx * dx) / (2 * 22 * 22)), 260),
      station(470, 'Memory foam', 'fs', 'fm', 32, (dx) => { const u = Math.min(1, Math.abs(dx) / 78); return Math.pow((1 + Math.cos(Math.PI * u)) / 2, 0.55); }, 1700),
    ];
    const draw = (s, d, orbY) => {
      let top = '';
      for (let x = -HALF; x <= HALF; x += 4) top += (x === -HALF ? 'M' : ' L') + (s.cx + x) + ' ' + (T0 + s.depth * d * s.profile(x)).toFixed(1);
      const path = `${top} L${s.cx + HALF} ${B - 10} Q${s.cx + HALF} ${B} ${s.cx + HALF - 10} ${B} H${s.cx - HALF + 10} Q${s.cx - HALF} ${B} ${s.cx - HALF} ${B - 10} Z`;
      set(s.slab, { d: path }); set(s.texture, { d: path });
      set(s.orb, { transform: `translate(${s.cx} ${orbY.toFixed(1)})` });
      const contact = Math.max(0, 1 - Math.abs(orbY + R - (T0 + s.depth * d)) / 30);
      set(s.shadow, { cy: (T0 + s.depth * d - 2).toFixed(1), rx: (R * (0.6 + 0.4 * contact)).toFixed(1), ry: 5, opacity: (0.15 + 0.35 * contact).toFixed(2) });
      const wide = s.depth < 40;
      set(s.press, { rx: ((wide ? 118 : 30) * (0.4 + 0.6 * d)).toFixed(1), ry: (wide ? 9 : 8).toFixed(1), opacity: ((wide ? 0.5 : 1) * d).toFixed(2) });
      set(s.ring, { opacity: !wide && d > 0.92 ? ((d - 0.92) * 12).toFixed(2) : 0 });
    };
    const P = 5600, orbTop = 118;
    const frame = (now) => {
      const t = now % P;
      S.forEach((s) => {
        const rest = T0 - R;                         // orb resting on the flat surface
        let orbY, target;
        if (t < 1500) { const k = ease(t / 1500); orbY = lerp(orbTop, rest + s.depth, k); target = clamp((orbY - rest) / s.depth, 0, 1); s.d = target; }
        else if (t < 3300) { orbY = rest + s.depth; s.d = 1; }
        else { const k = ease(Math.min(1, (t - 3300) / 700)); orbY = lerp(rest + s.depth, orbTop, k); s.d = Math.max(0, 1 - (t - 3300) / s.rebound); s.d = s.d * s.d * (3 - 2 * s.d); }
        draw(s, s.d, Math.min(orbY, rest + s.depth * s.d));
      });
      raf = requestAnimationFrame(frame);
    };
    let raf = 0;
    S.forEach((s) => draw(s, 1, T0 - R + s.depth));
    if (!reduce) whileVisible(sec, () => { if (!raf) raf = requestAnimationFrame(frame); }, () => { cancelAnimationFrame(raf); raf = 0; });
  });

  runScrubs();
})();
